import React, { useRef, useMemo, useState, useCallback, useEffect } from "react";
import clsx from "clsx";
import { createUseStyles } from "react-jss";
import { useWindowSize } from "../../lib/player/src/hooks";
import { getPins } from "../../lib/player/src/Customisation";
import { DragSource, DropTarget } from "react-dnd";

const useStyles = createUseStyles(theme => ({
  centerer: {
    width: "100%",
    height: "100%",
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 999,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  backgroundHider: {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
  },
  modalContainer: ({ contentWidth, contentHeight }) => ({
    width: `${contentWidth}px`,
    height: `${contentHeight}px`,
    maxHeight: "90vh",
    background: theme.colorDarkGrey,
    zIndex: 1000,
    boxShadow: "2px 0px 16px rgba(0, 0, 0, 0.27)",
    borderRadius: 3,
    color: "white",
    position: "relative",
  }),
  close: {
    position: "absolute",
    left: -16,
    top: -16,
    zIndex: 1001,
    cursor: "pointer",
    backgroundImage: "url(/assets/close-hover.png)",
    backgroundSize: "contain",
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  modalContent: {
    width: "100%",
    height: "100%",
    position: "relative",
    backgroundSize: "cover",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
  },
  deck: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    width: "100%",
    height: "auto",
  },
  hotspot: {
    backgroundImage: props => `url("${props.pins.default}")`,
    backgroundSize: "cover",
    cursor: "move",
    width: 25,
    height: 25,
    position: "absolute",
    "transition-duration": "0.4s",
    overflow: "hidden",
    transition: "opacity 0.2s ease",
    opacity: 1,
    "&:hover": {
      backgroundImage: props => `url("${props.pins.hover}")`,
      backgroundSize: "cover",
      height: 36,
      marginTop: "-11px",
      overflow: "visible",
    },
  },
  hotspotDragging: {
    extend: "hotspot",
    opacity: 0.5,
    cursor: "grabbing",
  },
  hotspotHover: {
    backgroundImage: props => `url("${props.pins.hover}")`,
    backgroundSize: "cover",
    height: 36,
    marginTop: "-11px",
    overflow: "visible",
  },
  hotspotContent: {
    opacity: 0,
    height: "175px",
    width: "225px",
    position: "absolute",
    top: "-175px",
    left: "50%",
    transform: "translateX(-50%)",
    "transition-duration": "1s",
  },
  hotspotContentActive: {
    extend: "hotspotContent",
    opacity: 1,
  },
  hotspotImageContainer: {
    width: "100%",
    height: "calc(100% - 10px)",
    overflow: "hidden",
    borderRadius: 7,
    position: "relative",
    border: "2px solid white",
    boxShadow: "0px 1px 10px #25252559",
    backgroundColor: "rgba(23, 190, 218, 0.7)",
    animationName: "fadein",
    animationDuration: "0.6s",
    "animation-timing-function": "ease-in",
  },
  "@keyframes fadein": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  hotspotImage: {
    height: "100%",
  },
  hotspotPlay: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 27,
    opacity: 0.75,
    color: "white",
    "&:hover": {
      top: 4,
      opacity: 1,
    },
  },
  infoText: {
    flex: 1,
    padding: 9,
    margin: 0,
    textAlign: "left",
    color: "white",
    fontSize: "12px",
    fontWeight: 400,
    overflow: "hidden",
  },
  imageInfo: {
    position: "absolute",
    display: "flex",
    flexDirection: "row",
    height: "45px",
    top: 0,
    width: "100%",
    background: "rgba(0, 0, 0, 0.45)",
    alignItems: "center",
    borderRadius: [7, 7, 0, 0],
  },
}));

const HotspotDeckPreview = ({
  show,
  onClose,
  backgroundImage,
  floorplan,
  images = [],
  tour,
  activeDeck,
  onHotspotPositionChange, // New prop for saving position changes
}) => {
  const size = useWindowSize();
  const [activeHotspot, setActiveHotspot] = useState();
  const [draggingUuid, setDraggingUuid] = useState('');
  const draggingOffsets = useRef({});
  const modalContentBounds = useRef({ left: 0, right: 0 });

  const contentWidth = size.width * 0.9;
  const contentHeight = Math.min(contentWidth * 9 / 16, size.height * 0.9);
  const floorplans = tour?.data?.floorplans || [];
  const classes = useStyles({
    pins: getPins(),
    contentWidth,
    contentHeight,
  });

  useEffect(() => {
    if (!show) return;
    const updateBounds = () => {
      const element = document.querySelector(`.${classes.modalContent}`);
      if (element) {
        const rect = element.getBoundingClientRect();
        modalContentBounds.current = {
          left: rect.left,
          top: rect.top
        };
      }
    };

    updateBounds();
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, [show]);

  const { 
    heightMultiplier: heightMultiplierProp,
    externalAdjust = 0,
  } = tour?.data?.options?.hotspotPage || {}
  const medias = tour.data.options?.floorplans?.medias || []

  const floorplanMarginTop = useMemo(() => {
    const level = floorplans.findIndex(item => item.id === activeDeck) - Math.round((floorplans.length + 1) / 2);

    const heightMultiplier =
      heightMultiplierProp === undefined ? 30 : heightMultiplierProp * 36;

    const hotspotHeight =
      ((contentWidth > contentHeight ? contentWidth : contentHeight) / 970) ** 0.92;

    const movement =
      hotspotHeight * heightMultiplier * level + externalAdjust * hotspotHeight;
    return movement;
  }, [activeDeck, floorplans, heightMultiplierProp, contentWidth, contentHeight, externalAdjust])

  let boatWidth = 0;
  if (contentWidth > contentHeight) {
    boatWidth = contentWidth * 0.7;
  } else {
    boatWidth = contentHeight * 0.7;
  }

  const modalBoatWidth = Math.min(boatWidth, contentWidth * 0.8);
  const modalBoatLeft = (contentWidth - modalBoatWidth) / 2;

  // Handle mouse drag for hotspots
  const handleMouseDown = useCallback((e, hotspotUuid) => {
    e.preventDefault();
    setDraggingUuid(hotspotUuid);
    setActiveHotspot(undefined);
    
    const handleMouseMove = (moveEvent) => {
      const relativeY = moveEvent.clientY - modalContentBounds.current.top;
      const offsetY = (relativeY - floorplanMarginTop - 12.5) / contentHeight;
      
      draggingOffsets.current = { 
        offsetX: (moveEvent.clientX - modalContentBounds.current.left - modalBoatLeft - 12.5) / modalBoatWidth,
        offsetY: offsetY,
      };
    };
    
    const handleMouseUp = () => {
      setDraggingUuid('');
      onHotspotPositionChange?.(hotspotUuid, draggingOffsets.current);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [modalBoatWidth, contentHeight, onHotspotPositionChange, floorplanMarginTop]);

  const getImageData = (imageId) => {
    const image = images.find(item => item.id === imageId);
    if (image) {
      return image;
    }

    const media = medias.find(i => i.id === imageId);
    if (media) {
      const imageElement = media.options?.elements?.find(i => i.type === "image");
      return {
        name: media.options?.title || "",
        urls: {
          low: imageElement?.image?.preview_url || "",
        },
        id: imageId,
      };
    }

    return {
      name: "Unknown",
      urls: {
        low: "",
      },
      id: imageId,
    };
  };

  if (!show || !floorplan) {
    return null;
  }

  return (
    <div 
      className={classes.centerer}
    >
      <div
        className={classes.backgroundHider}
        onClick={onClose}
      />
      <div className={classes.modalContainer}>
        <div className={classes.close} onClick={onClose} />
        <div 
          className={classes.modalContent}
          style={{
            backgroundImage: backgroundImage ? `url("${backgroundImage}")` : 'none',
          }}
        >
          <div 
            className={classes.deck}
            style={{
              left: `${modalBoatLeft}px`,
              width: `${modalBoatWidth}px`,
            }}
          >
            <div>
              {floorplan.hotspots && floorplan.hotspots.map((hotspot, index) => {
                const imageData = getImageData(hotspot.image);
                const isActive = activeHotspot === index && !draggingUuid;
                const isDraggingThis = draggingUuid === hotspot.uuid;
                const cordX = (hotspot.offsetX || hotspot.x) * modalBoatWidth;

                const cordY = hotspot.offsetY !== undefined 
                  ? hotspot.offsetY * contentHeight + floorplanMarginTop - contentHeight / 2
                  : floorplanMarginTop;
                
                return (
                  <div
                    key={hotspot.uuid}
                    className={clsx(
                      classes.hotspot,
                      isActive ? classes.hotspotHover : "",
                      isDraggingThis ? classes.hotspotDragging : ""
                    )}
                    style={{
                      left: `${cordX}px`,
                      top: `${cordY}px`,
                    }}
                    onMouseEnter={() => !draggingUuid && setActiveHotspot(index)}
                    onMouseLeave={() => !draggingUuid && setActiveHotspot(undefined)}
                    onMouseDown={(e) => handleMouseDown(e, hotspot.uuid)}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {isActive && (
                      <div className={classes.hotspotContentActive}>
                        <div className={classes.hotspotImageContainer}>
                          {imageData.urls.low && (
                            <img
                              className={classes.hotspotImage}
                              src={imageData.urls.low}
                              alt={imageData.name}
                            />
                          )}
                          <div className={classes.imageInfo}>
                            <p className={classes.infoText}>{imageData.name}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotspotDeckPreview;
